import { TablePagination } from "../../../components/ui/TablePagination";
import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { Trans } from "react-i18next";import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Download, Eye, EyeOff, ChevronDown, Users } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { LoadingState } from "../../../components/ui";
import {
  fetchAnnouncementById,
  saveAnnouncementDocument,
  fetchDepartments,
  fetchManagers,
  toLocalISODate,
  reportReadAnnouncementView,
  reportUnreadAnnouncementView } from
"../../../services/announcementServices";
import { ROUTES, STATUS_STYLES, IMPORTANT_COLOR, GENERAL_COLOR, toUiStatus, formatDate } from "../constants/announcementOptions";

function normaliseItem(raw) {
  const publishedDate = raw.announcementStatus === "Published" && raw.updatedOn ? formatDate(raw.updatedOn) : "";
  const rawExp = raw.archiveDate ?? raw.announcementArchiveDate ?? raw.expiryOn ?? raw.endDate ?? raw.validUntil ?? null;
  const isImportant = (raw.announcementType ?? "").toLowerCase() === "important";
  return {
    id: String(raw.announcementId),
    announcementId: raw.announcementId,
    title: raw.announcementTitle ?? "",
    priority: raw.announcementType ?? "General",
    priorityColor: isImportant ? IMPORTANT_COLOR : GENERAL_COLOR,
    barColor: isImportant ? IMPORTANT_COLOR : GENERAL_COLOR,
    createdDate: formatDate(raw.createdOn),
    publishedDate,
    archiveDate: formatDate(rawExp),
    archiveDateISO: toLocalISODate(rawExp),
    audience: raw.recipients ?? "",
    departmentId: raw.departmentId ?? 0,
    managerId: raw.managerId ?? 0,
    departmentName: raw.departmentName ?? raw.deptName ?? raw.department_name ?? null,
    managerName: raw.managerName ?? raw.managerFirstName ?? raw.manager_name ?? null,
    status: toUiStatus(raw.announcementStatus),
    content: raw.announcementBody ?? "",
    remarks: raw.remarks ?? "",
    hasSupportingDocument: raw.hasSupportingDocument ?? false
  };
}

export function CreatorAnnouncementDetailPage() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  // Keep each report's result and pagination independent. The API paginates
  // `Opened` and `New` records separately, so their page counts can differ.
  const [readViewers, setReadViewers] = useState([]);
  const [readPage, setReadPage] = useState(0);
  const [readTotalPages, setReadTotalPages] = useState(1);
  const [readPageSize, setReadPageSize] = useState(10);
  const [readTotalElements, setReadTotalElements] = useState(0);
  const [readLoading, setReadLoading] = useState(false);
  const [unreadViewers, setUnreadViewers] = useState([]);
  const [unreadPage, setUnreadPage] = useState(0);
  const [unreadTotalPages, setUnreadTotalPages] = useState(1);
  const [unreadPageSize, setUnreadPageSize] = useState(10);
  const [unreadTotalElements, setUnreadTotalElements] = useState(0);
  const [unreadLoading, setUnreadLoading] = useState(false);
  const [readOpen, setReadOpen] = useState(false);
  const [unreadOpen, setUnreadOpen] = useState(false);

  const fetchReadViewers = useCallback(async (annId, page) => {
    if (!annId) return;
    setReadLoading(true);
    try {
      const result = await reportReadAnnouncementView(annId, page);
      setReadViewers(result.content);
      setReadTotalPages(result.totalPages);
      setReadTotalElements(result.totalElements);
      setReadPageSize(result.size);
    } catch {
      // silently ignore — viewer list is supplementary
    } finally {
      setReadLoading(false);
    }

   }, []);
    const fetchUnreadViewers = useCallback(async (annId, page) => {
    if (!annId) return;
    setUnreadLoading(true);
    try {
      const result = await reportUnreadAnnouncementView(annId, page);
      setUnreadViewers(result.content);
      setUnreadTotalPages(result.totalPages);
      setUnreadTotalElements(result.totalElements);
      setUnreadPageSize(result.size);
    } catch {
      // silently ignore — viewer list is supplementary
    } finally {
      setUnreadLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [resolvedAnnId, setResolvedAnnId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchAnnouncementById(id)
    .then(async (raw) => {
      if (cancelled) return;
      if (!raw) {setFetchError("Announcement not found.");return;}
      const base = normaliseItem(raw);
      let { departmentName, managerName } = base;
      if (!departmentName && (raw.departmentId ?? 0) > 0) {
        const depts = await fetchDepartments().catch(() => []);
        if (!cancelled) departmentName = depts.find((d) => d.departmentId === raw.departmentId)?.departmentName ?? null;
      }
      if (!managerName && (raw.managerId ?? 0) > 0) {
        const mgrs = await fetchManagers().catch(() => []);
        if (!cancelled) managerName = mgrs.find((m) => m.managerId === raw.managerId)?.managerName ?? null;
      }
      if (cancelled) return;
      setItem({ ...base, departmentName, managerName });
      const annId = raw.announcementId ?? id;
      setResolvedAnnId(annId);
      setReadPage(0);
      setUnreadPage(0);
      fetchReadViewers(annId, 0);
      fetchUnreadViewers(annId, 0);
    })
    .catch((err) => { if (!cancelled) setFetchError(`Failed to load: ${err.message}`); })
    .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // fetchViewers is stable (useCallback with []), safe to omit from deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleReadPageChange = (newPage) => {
    setReadPage(newPage);
    fetchReadViewers(resolvedAnnId, newPage);
  };

  const handleUnreadPageChange = (newPage) => {
    setUnreadPage(newPage);
    fetchUnreadViewers(resolvedAnnId, newPage);
  };


  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      await saveAnnouncementDocument(item.announcementId);
    } catch (err) {
      setDownloadError(`Download failed: ${err.message}`);
    } finally {
      setDownloading(false);
    }
  };



  if (loading) return <AppLayout formLayout="standard"><LoadingState /></AppLayout>;
  if (fetchError || !item) {
    return (
      <AppLayout formLayout="standard">
        <p className="p-8 text-sm text-error">{fetchError ?? "Announcement not found."}</p>
      </AppLayout>);

  }

  const statusStyle = STATUS_STYLES[item.status] ?? STATUS_STYLES.Submitted;
  const Field = ({ label, value, style }) =>
  <div>
      <p className="text-xs font-medium uppercase per-file-hint mb-1.5">{label}</p>
      <p className="text-sm text-foreground" style={style}>{value}</p>
    </div>;


  return (
    <AppLayout formLayout="standard">
        <PageTitleRow back={<PageBackLink to={ROUTES.creatorList} />}>
          <h1 className="text-2xl font-bold text-foreground mb-6 wrap-break-word">{item.title}</h1>
        </PageTitleRow>
      <div className="bg-white rounded-2xl p-7 sm:p-8 w-full shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] border-l-4"
      style={{ borderLeftColor: item.barColor }}>
        <div className="flex items-center gap-3 mb-4">
          <span className="flex items-center gap-2 text-sm font-medium" style={{ color: item.priorityColor }}>
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.priorityColor }} />
            {item.priority}
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[13px] font-semibold"
          style={{ backgroundColor: statusStyle.bg, color: statusStyle.color }}>
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: statusStyle.dot }} />
            {item.status}
          </span>
        </div>



        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-(--color-text-border)/70 pt-6">
          <Field label="Created On" value={item.createdDate || "—"} />
          <Field label="Archive Date" value={item.archiveDate || "—"} />
          {item.status === "Published" &&
          <Field label="Published On" value={item.publishedDate || "—"} style={{ color: "var(--status-published-fg)", fontWeight: 500 }} />
          }
          <div>
            <p className="text-xs font-medium uppercase per-file-hint mb-1.5"><Trans i18nKey="common.recipients" /></p>
            <p className="text-sm text-foreground">
              {item.audience || "—"}
              {item.audience === "Department" && item.departmentName &&
              <span className="per-file-hint"> — {item.departmentName}</span>
              }
              {item.audience === "Reportees" && item.managerName &&
              <span className="per-file-hint"> <Trans i18nKey="announcements.mgr" /> {item.managerName}</span>
              }
            </p>
          </div>
          <Field label="Category" value={item.priority} />
        </div>

        <div className="mt-6 border-t border-(--color-text-border)/70 pt-6">
          <p className="text-xs font-medium uppercase per-file-hint"><Trans i18nKey="announcements.content" /></p>
          <p className="text-sm leading-relaxed text-foreground mt-2 whitespace-pre-line wrap-break-word">{item.content}</p>
        </div>

        {item.status === "Required Corrections" && item.remarks && item.remarks.trim() &&
        <div className="mt-6 border-t border-(--color-text-border)/70 pt-6">
            <p className="text-xs font-medium uppercase per-file-hint"><Trans i18nKey="announcements.hrRemarks" /></p>
            <p className="text-sm leading-relaxed mt-2" style={{ color: "var(--status-warning-fg)" }}>{item.remarks}</p>
          </div>
        }

        <div className="mt-6 border-t border-(--color-text-border)/70 pt-6">
          <p className="text-xs font-medium uppercase per-file-hint"><Trans i18nKey="common.attachments" /></p>
          {item.hasSupportingDocument ?
          <div className="mt-2">
              <button type="button" onClick={handleDownload} disabled={downloading}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-(--color-text-border) bg-workspace/40 text-sm text-foreground hover:bg-workspace disabled:opacity-60">
                <Download className="h-4 w-4" />
                {downloading ? "Downloading…" : "Download Document"}
              </button>
              {downloadError && <p className="mt-1.5 text-[13px] text-error">{downloadError}</p>}
            </div> :

          <p className="mt-2 text-sm per-file-hint italic"><Trans i18nKey="announcements.noDocumentsAttached" /></p>
          }
        </div>

        {/* ── Viewer Read/Unread Columns ── */}
        {(() => {
          const totalViewers = readTotalElements + unreadTotalElements;
          const viewersLoading = readLoading || unreadLoading;

          return (
            <div className="mt-6 border-t border-(--color-text-border)/70 pt-6">
              {/* Total Viewers row */}
              <div className="flex items-center gap-2.5 mb-5">
                <Users className="h-4.5 w-4.5 text-primary" />
                <p className="text-sm font-semibold text-foreground">
                  Total Viewers
                </p>
                <span className="inline-flex items-center justify-center h-5 min-w-[1.4rem] px-1.5 rounded-full text-[11px] font-bold bg-primary text-white">
                  {totalViewers}
                </span>
                {viewersLoading && (
                  <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-primary border-t-transparent animate-spin ml-1" />
                )}
              </div>

              {!viewersLoading && totalViewers === 0 ? (
                <p className="text-sm per-file-hint italic">No viewer data yet.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* ── Read column ── */}
                  <div className="rounded-xl border border-(--color-text-border)/70 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setReadOpen(prev => !prev)}
                      className="w-full flex items-center justify-between gap-3 px-5 py-3.5 bg-(--status-published-bg,#dcfce7)/40 hover:bg-(--status-published-bg,#dcfce7)/70 transition cursor-pointer"
                    >
                      <span className="flex items-center gap-2 text-sm font-semibold text-(--status-published-fg,#15803d)">
                        <Eye className="h-4 w-4" />
                        Read
                        <span className="inline-flex items-center justify-center h-5 min-w-[1.3rem] px-1.5 rounded-full text-[11px] font-bold bg-(--status-published-fg,#15803d) text-white">
                          {readTotalElements}
                        </span>
                      </span>
                      <ChevronDown className={`h-4 w-4 text-(--status-published-fg,#15803d) transition-transform duration-200 ${readOpen ? "rotate-180" : ""}`} />
                    </button>
                    {readOpen && (
                      <div className="px-4 py-3 flex flex-col gap-1.5 bg-white">
                        {readViewers.length === 0 ? (
                          <p className="text-sm per-file-hint italic py-1">None on this page</p>
                        ) : readViewers.map((v, i) => (
                          <div key={i} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-workspace/50 transition">
                            <span className="h-7 w-7 rounded-full bg-(--status-published-bg,#dcfce7) text-(--status-published-fg,#15803d) grid place-items-center text-[11px] font-bold shrink-0">
                              {(v.empName ?? "?")[0].toUpperCase()}
                            </span>
                            <span className="text-sm text-foreground truncate">{v.empName || "—"}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {readOpen && (
                      <div className="px-4 pb-3 bg-white">
                        <TablePagination
                          label="Read"
                          page={readPage + 1} totalItems={readTotalElements} pageSize={readPageSize}
                          totalPages={readTotalPages}
                          disabled={readLoading}
                          onPageChange={(next) => handleReadPageChange(next - 1)}
                        />
                      </div>
                    )}
                  </div>

                  {/* ── Unread column ── */}
                  <div className="rounded-xl border border-(--color-text-border)/70 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setUnreadOpen(prev => !prev)}
                      className="w-full flex items-center justify-between gap-3 px-5 py-3.5 bg-(--status-archived-bg,#f1f5f9)/60 hover:bg-(--status-archived-bg,#f1f5f9) transition cursor-pointer"
                    >
                      <span className="flex items-center gap-2 text-sm font-semibold per-file-hint">
                        <EyeOff className="h-4 w-4" />
                        Unread
                        <span className="inline-flex items-center justify-center h-5 min-w-[1.3rem] px-1.5 rounded-full text-[11px] font-bold bg-(--color-text-secondary) text-white">
                          {unreadTotalElements}
                        </span>
                      </span>
                      <ChevronDown className={`h-4 w-4 per-file-hint transition-transform duration-200 ${unreadOpen ? "rotate-180" : ""}`} />
                    </button>
                    {unreadOpen && (
                      <div className="px-4 py-3 flex flex-col gap-1.5 bg-white">
                        {unreadViewers.length === 0 ? (
                          <p className="text-sm per-file-hint italic py-1">None on this page</p>
                        ) : unreadViewers.map((v, i) => (
                          <div key={i} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-workspace/50 transition">
                            <span className="h-7 w-7 rounded-full bg-(--status-archived-bg,#f1f5f9) per-file-hint grid place-items-center text-[11px] font-bold shrink-0">
                              {(v.empName ?? "?")[0].toUpperCase()}
                            </span>
                            <span className="text-sm text-foreground truncate">{v.empName || "—"}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {unreadOpen && (
                      <div className="px-4 pb-3 bg-white">
                        <TablePagination
                          label="Unread"
                          page={unreadPage + 1} totalItems={unreadTotalElements} pageSize={unreadPageSize}
                          totalPages={unreadTotalPages}
                          disabled={unreadLoading}
                          onPageChange={(next) => handleUnreadPageChange(next - 1)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

      </div>
    </AppLayout>);

}
